/**
 * SupplierInvoicesUploadModal — Multi-File-Upload fuer Lieferanten-Rechnungen.
 *
 * Per-Row: supplier, category, amount, issuedOn, dueOn. Parallele Uploads via
 * useMultiFileUpload. Backend macht atomic Create (PropertyFile + SupplierInvoice) pro Row.
 */
import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
} from '@/shared/components';
import type { CreateSupplierInvoiceRequest } from '@/shared/api';
import { AddMoreFiles, DropZone } from './AkteUploadAffordances';
import { SupplierInvoicesUploadRow } from './SupplierInvoicesUploadRow';
import {
  isInvoiceRowValid,
  newSupplierInvoiceRow,
  toCreateInvoiceRequest,
  type SupplierInvoiceRow,
} from './supplierInvoicesUploadModel';
import { useMultiFileUpload } from './useMultiFileUpload';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpload: (file: File, metadata: CreateSupplierInvoiceRequest) => Promise<void>;
  initialFiles?: File[];
}

const HEADERS = ['Datei', 'Lieferant *', 'Kategorie *', 'Betrag *', 'Datum *', 'Fällig', ''];

export function SupplierInvoicesUploadModal({ open, onOpenChange, onUpload, initialFiles }: Props) {
  const [defaultCurrency, setDefaultCurrency] = useState('EUR');

  const m = useMultiFileUpload<SupplierInvoiceRow, CreateSupplierInvoiceRequest>({
    buildRow: (f) => newSupplierInvoiceRow(f, defaultCurrency),
    isValid: isInvoiceRowValid,
    toMeta: toCreateInvoiceRequest,
    upload: onUpload,
    invalidMessage: 'Bitte fülle Lieferant, Betrag, Datum und Kategorie für alle Zeilen aus.',
    noneMessage: 'Keine Rechnung angelegt — bitte Fehler prüfen',
    partialMessage: (s, t) => `${s} von ${t} angelegt — Rest siehe Fehlermeldungen`,
    successMessage: (n) => `${n} ${n === 1 ? 'Rechnung' : 'Rechnungen'} angelegt`,
    onAllDone: () => onOpenChange(false),
  });

  useEffect(() => {
    if (open && initialFiles && initialFiles.length > 0) {
      m.addFilesArray(initialFiles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialFiles]);

  function handleClose(v: boolean) {
    if (!v && m.uploading) return;
    onOpenChange(v);
    if (!v) m.reset();
  }

  const title =
    m.rows.length === 0
      ? 'Rechnungen hochladen'
      : `${m.rows.length} ${m.rows.length === 1 ? 'Rechnung' : 'Rechnungen'} hochladen`;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-5xl" showClose={!m.uploading}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {m.rows.length === 0 ? (
            <DropZone
              onFiles={m.addFiles}
              hint="Mehrere PDFs auf einmal — pro Datei eine Rechnung"
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-ink-faint border-line/40 border-b text-left text-[10px] uppercase tracking-wider">
                  <tr>
                    {HEADERS.map((h, i) => (
                      <th
                        key={i}
                        className={i === HEADERS.length - 1 ? 'w-12 py-2' : 'py-2 pr-2 font-medium'}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {m.rows.map((row) => (
                    <SupplierInvoicesUploadRow
                      key={row.id}
                      row={row}
                      defaultCurrency={defaultCurrency}
                      onChange={(patch) => m.patchRow(row.id, patch)}
                      onRemove={() => m.removeRow(row.id)}
                      disabled={m.uploading}
                    />
                  ))}
                </tbody>
              </table>
              <AddMoreFiles onFiles={m.addFiles} disabled={m.uploading} />
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <div className="text-ink-faint mr-auto flex items-center gap-2 text-xs">
            <span>Standard-Währung</span>
            <Select
              value={defaultCurrency}
              onChange={(e) => setDefaultCurrency(e.target.value)}
              disabled={m.uploading}
              className="w-24! py-1.5! text-xs"
            >
              <option value="EUR">EUR</option>
              <option value="USD">USD</option>
              <option value="CHF">CHF</option>
            </Select>
          </div>
          <Button variant="ghost" onClick={() => handleClose(false)} disabled={m.uploading}>
            Abbrechen
          </Button>
          <Button variant="brand" disabled={!m.canSubmit} loading={m.uploading} onClick={m.submit}>
            {m.rows.length === 0
              ? 'Hochladen'
              : `${m.rows.length} ${m.rows.length === 1 ? 'Rechnung' : 'Rechnungen'} anlegen`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
